/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import Link from '@/components/UI/Link';
import type { FilterOption, TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import UserAvatar from '@/components/UI/UserAvatar';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { fetchMetadataColumns, formatNumber } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { MetadataFieldTypes, MetadataFieldWithValue } from '@/model/Metadata.model';
import type { Sample, SampleMeasurement } from '@/model/Sample.model';
import { getSampleStatusDisplay } from '@/model/Sample.model';
import type { BaseUser } from '@/model/User.model';
import { web as webRoute } from '@/support/route';
import { DateRenderFormat, DateTimeRenderFormat, DateUtils } from '@/utils/Date.utils';
import { Study } from '@/model/Study.model.ts';
import { containsOperation, notContainsOperation, selectOperations } from '@/utils/FiltersOperations.utils.ts';
import {
  AddSamplePlanContext,
  isSampleData,
  SampleData,
  TempSampleData,
} from '@/components/Samples/AddSamplePlanProvider.tsx';
import { TeamGlossaryApiId } from '@/model/Glosary.model.ts';
import { TempSampleTypeId } from '@/model/SamplePlan.model';

export interface SampleExtended extends Sample {
  study_link: Study & {
    access: string;
  };
}

const accessSet = new Set(['read', 'write']);

export const buildColumns = (
  groupMetadata: Array<MetadataFieldWithValue> = [],
  sampleMetadata: Array<MetadataFieldWithValue> = [],
  columns: Record<string, boolean>,
  isStudy: boolean,
  plannedSamplesEnabled = false
): Array<TanStackColumnDef<Sample>> => {
  const cellWidth = 100;

  const statusColumn: TanStackColumnDef<Sample> = {
    id: 'status',
    header: 'Sample Status',
    accessorKey: 'status',
    width: cellWidth,
    cell: ({ row: { original } }: { row: { original: Sample } }) => getSampleStatusDisplay(original?.status),
    isVisible: columns?.status ?? true,
  };

  const timepointColumn: TanStackColumnDef<Sample> = {
    id: 'timepoint',
    header: 'Timepoint',
    accessorKey: 'timepoint',
    width: cellWidth,
    cell: ({ row: { original } }: { row: { original: Sample } }) =>
      DateUtils.renderDuration(original?.timepoint, { format: 'short', zeros: true, defaultResponse: '-' }),
    isVisible: columns?.timepoint ?? true,
  };

  let tableColumns: Array<TanStackColumnDef<Sample>> = [
    {
      id: 'sample_id',
      header: 'Sample ID',
      accessorKey: 'sample_id',
      size: cellWidth,
      cell: ({ row: { original } }: { row: { original: Sample } }) => original?.sample_id ?? '-',
      isVisible: columns?.sample_id ?? true,
    },
    ...(plannedSamplesEnabled ? [statusColumn] : []),
    {
      id: 'subject',
      header: 'Animal',
      accessorKey: 'subject',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => {
        const { original } = row;
        if (_isNil(original?.subject?.id)) {
          return '-';
        }
        return accessSet.has((original as SampleExtended)?.study_link.access) ? (
          <Link
            className="link blue"
            to={webRoute('animals.show', {
              id: original.study_id,
              subject_id: original?.subject?.id,
            })}
          >
            {original?.subject?.name}
          </Link>
        ) : (
          original?.subject?.name
        );
      },
      isVisible: columns?.subject ?? true,
    },
    {
      id: 'tail',
      header: 'Tail',
      accessorFn: (row: Sample) => row.subject?.alt_ids?.tail,
      accessorPath: 'subject.alt_ids.tail',
      enableSorting: false,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.subject?.alt_ids?.tail ?? '-',
      isVisible: columns?.tail,
    },
    {
      id: 'donor',
      header: 'Donor',
      accessorFn: (row: Sample) => row.subject?.alt_ids?.donor,
      accessorPath: 'subject.alt_ids.donor',
      enableSorting: false,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.subject?.alt_ids?.donor ?? '-',
      isVisible: columns?.donor,
    },
    {
      id: 'ear',
      header: 'Ear',
      accessorFn: (row: Sample) => row.subject?.alt_ids?.ear,
      accessorPath: 'subject.alt_ids.ear',
      enableSorting: false,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.subject?.alt_ids?.ear ?? '-',
      isVisible: columns?.ear,
    },
    {
      id: 'tag',
      header: 'Tag',
      accessorFn: (row: Sample) => row.subject?.alt_ids?.tag,
      accessorPath: 'subject.alt_ids.tag',
      enableSorting: false,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.subject?.alt_ids?.tag ?? '-',
      isVisible: columns?.tag,
    },
    {
      id: 'collection',
      header: 'Cage',
      accessorKey: 'collection',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.collection?.name ?? '-',
      isVisible: columns?.collection ?? true,
    },
    {
      id: 'date',
      header: 'Date taken',
      accessorKey: 'date',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => (
        <DateTimeRenderer value={row.original?.date} format={DateRenderFormat.Date} defaultResponse="-" />
      ),
      isVisible: columns?.date ?? true,
    },
    {
      id: 'time',
      header: 'Time taken',
      accessorFn: (row: Sample) => row.date,
      accessorPath: 'time',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => (
        <DateTimeRenderer value={row.original?.date} format={DateTimeRenderFormat.Time} defaultResponse="-" />
      ),
      isVisible: columns?.time ?? true,
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => row.original?.type ?? '-',
      isVisible: columns?.type ?? true,
    },
    {
      id: 'user',
      header: 'User',
      accessorKey: 'user',
      size: cellWidth,
      cell: ({ row }: { row: { original: Sample } }) => (
        <UserAvatar user={row.original?.user} tooltip={row.original?.user?.name ?? row.original?.user?.email} />
      ),
      isVisible: columns?.user ?? true,
    },
    {
      id: 'study_group',
      header: 'Group',
      accessorKey: 'study_group',
      size: 150,
      meta: { style: { overflow: 'visible' } },
      cell: ({ row }: { row: { original: Sample } }) => <GroupLabel group={row.original?.study_group} />,
      isVisible: columns?.study_group ?? true,
    },
    ...(plannedSamplesEnabled ? [timepointColumn] : []),
  ];

  if (isStudy) {
    tableColumns = [
      ...tableColumns,
      {
        id: 'comments',
        header: 'Comments',
        accessorKey: 'comments',
        enableSorting: false,
        size: cellWidth,
        cell: ({ row }: { row: { original: Sample } }) => row.original?.comments ?? '-',
        isVisible: columns?.comments ?? true,
      },
      {
        id: 'weight',
        header: 'Weight',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.weight',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'weight'),
        isVisible: columns?.weight,
      },
      {
        id: 'volume',
        header: 'Volume',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.volume',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'volume'),
        isVisible: columns?.volume,
      },
      {
        id: 'length',
        header: 'Length',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.length',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'length'),
        isVisible: columns?.length,
      },
      {
        id: 'depth',
        header: 'Depth',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.depth',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'depth'),
        isVisible: columns?.depth,
      },
      {
        id: 'width',
        header: 'Width',
        accessorFn: (row: Sample) => row.details,
        accessorPath: 'details?.width',
        enableSorting: false,
        cell: ({ row }: { row: { original: Sample } }) => processDetails(row.original?.details, 'width'),
        isVisible: columns?.width,
      },
    ];
  } else {
    tableColumns = [
      ...tableColumns,
      ...fetchMetadataColumns({
        metadata: groupMetadata,
        columns,
        nestedStudyLocation: 'study_group',
      }),
      {
        id: 'study',
        header: 'Study',
        accessorKey: 'study',
        enableSorting: false,
        size: cellWidth,
        cell: ({ row }: { row: { original: Sample } }) => {
          const { original } = row;
          if (_isNil(original['study_link']?.id)) {
            return '-';
          }
          return accessSet.has((original as SampleExtended)?.study_link.access) ? (
            <Link
              className="link blue"
              to={webRoute('studies.show', {
                id: original.study_link?.id,
              })}
            >
              {original.study_link?.name}
            </Link>
          ) : (
            original.study_link?.name
          );
        },
        isVisible: columns?.study ?? true,
      },
      {
        id: 'maxSubjects',
        header: 'Max Subjects',
        accessorKey: 'max_subjects',
        enableSorting: false,
        size: cellWidth,
        cell: ({ row }: { row: { original: Sample } }) => {
          return row.original?.study_group?.max_subjects ?? '-';
        },
        isVisible: columns?.max_subjects ?? true,
      },
    ];
  }

  return [...tableColumns, ...fetchMetadataColumns({ metadata: sampleMetadata, columns })];
};

const processDetails = (details: Array<SampleMeasurement>, key: string) => {
  const detailsInstance = details?.find((x) => x.key === key);
  if (_isEmpty(detailsInstance)) {
    return '-';
  }
  if (detailsInstance?.value) {
    return `${formatNumber(detailsInstance.value)} ${detailsInstance?.unit}`;
  }
  return `${detailsInstance?.value} ${detailsInstance?.unit}`;
};

export const generateFilterOptions = (
  users: Array<BaseUser>,
  studyId: ID,
  metadata: Array<MetadataFieldWithValue>,
  plannedSamplesEnabled = false
) => {
  const mapped = users.map(({ name, id: value }) => ({
    name,
    value,
  }));

  const statusFilter: FilterOption = {
    value: 'status',
    name: 'Status',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: [
          { name: 'Planned', value: 'planned' },
          { name: 'Collected', value: 'collected' },
          { name: 'Cancelled', value: 'cancelled' },
        ],
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: [
          { name: 'Planned', value: 'planned' },
          { name: 'Collected', value: 'collected' },
          { name: 'Cancelled', value: 'cancelled' },
        ],
      },
    ],
  };

  const timepointFilter: FilterOption = {
    value: 'timepoint',
    name: 'Timepoint',
    operations: [
      {
        name: filterType.eq_interval,
        value: filterType.eq_interval,
      },
      {
        name: filterType.gt_interval,
        value: filterType.gt_interval,
      },
      {
        name: filterType.lt_interval,
        value: filterType.lt_interval,
      },
      {
        name: filterType.between_interval,
        value: filterType.between_interval,
      },
    ],
  };

  const filterOptions: Array<FilterOption> = [
    {
      value: 'type',
      name: 'Type',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
      ],
      fetchFromApi: {
        placeholder: 'Sample type',
        url: 'team-glossary.list',
        queryParams: { group: 'samples' },
        tableFilterMenuProperty: 'team_glossary_api_id',
      },
    },
    {
      value: 'user',
      name: 'User',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: mapped,
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: mapped,
        },
      ],
    },
    ...(plannedSamplesEnabled ? [statusFilter, timepointFilter] : []),
  ];

  if (_notNil(studyId)) {
    filterOptions.push({
      value: 'study_group',
      name: 'Study Group',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
        {
          name: filterType.IS_NULL,
          value: filterType.IS_NULL,
        },
        {
          name: filterType.IS_NOT_NULL,
          value: filterType.IS_NOT_NULL,
        },
      ],
      fetchFromApi: {
        placeholder: 'Search groups',
        url: 'studies.groups.list',
        params: {
          id: studyId,
        },
      },
    });
  } else {
    filterOptions.push({
      value: 'study',
      name: 'Study',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
      ],
      fetchFromApi: {
        placeholder: 'Search studies',
        url: 'studies.search',
      },
    });
  }

  if (_notNil(metadata)) {
    const metadataFilters =
      metadata?.map(
        (item: { id: ID; title: string; field_type: MetadataFieldTypes; options?: string[] }): FilterOption => {
          switch (item.field_type) {
            case 'date':
              return {
                value: item.id,
                name: item.title,
                operations: [
                  {
                    name: filterType.contains,
                    value: filterType.eq_date,
                  },
                  {
                    name: filterType.not_contains,
                    value: filterType.not_eq_date,
                  },
                ],
              };
            case 'multi_select': {
              return {
                value: item.id,
                name: item.title,
                operations: selectOperations,
                fetchFromApi: {
                  placeholder: 'Multiselect options',
                  url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
                  params: { metadataGlossaryId: item.id },
                  fieldType: 'multi_select',
                  useModernApi: true,
                },
              };
            }
            case 'select': {
              return {
                value: item.id,
                name: item.title,
                operations: selectOperations,
                fetchFromApi: {
                  placeholder: 'Select options',
                  url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
                  params: { metadataGlossaryId: item.id },
                  fieldType: 'select',
                  useModernApi: true,
                },
              };
            }

            default:
              return {
                value: item.id,
                name: item.title,
                operations: [...containsOperation, ...notContainsOperation],
              };
          }
        }
      ) ?? [];

    metadataFilters.forEach((filter) => filterOptions.push(filter));
  }

  return filterOptions;
};

export const validateManageSamplesSelection = (
  hasAccess: boolean,
  selectedSamples: Array<Sample> | undefined = []
): { disabled: boolean; tooltip: string } => {
  const defaultResult = {
    disabled: false,
    tooltip: '',
  };

  if (_isEmpty(selectedSamples)) {
    return defaultResult;
  }

  if (_isNotEmpty(selectedSamples)) {
    if (!hasAccess) {
      return {
        disabled: true,
        tooltip: 'You must have at least write access to the study to manage samples',
      };
    } else if (new Set(selectedSamples.map((x) => x.type)).size > 1) {
      return {
        disabled: true,
        tooltip: 'You cannot edit more than one type of sample at a time',
      };
    }
  }
  return defaultResult;
};

export const validateCancelSamplesSelection = (
  hasAccess: boolean,
  selectedSamples: Array<Sample> | undefined = []
): { disabled: boolean; tooltip: string } => {
  if (_isEmpty(selectedSamples)) {
    return {
      disabled: false,
      tooltip: '',
    };
  }

  if (!hasAccess) {
    return {
      disabled: true,
      tooltip: 'You must have at least write access to the study to cancel samples',
    };
  }

  // Check if all selected samples have planned status
  const nonPlannedSamples = selectedSamples.filter((sample) => sample.status !== 'planned');

  if (_isNotEmpty(nonPlannedSamples)) {
    return {
      disabled: true,
      tooltip: 'All selected samples must have planned status to cancel them',
    };
  }

  return {
    disabled: false,
    tooltip: '',
  };
};

export type SampleGlossaryEntry = AddSamplePlanContext['sampleGlossary'] extends Map<any, infer V> ? V : never;

/**
 * Checks if the current sample type is archived
 */
export const isCurrentSampleArchived = (
  sampleData: SampleData | TempSampleData | null,
  sampleGlossary: Map<TeamGlossaryApiId, SampleGlossaryEntry>
): boolean => {
  // Only SampleData has a type field (TempSampleData doesn't)
  if (!sampleData || !isSampleData(sampleData)) {
    return false;
  }

  const glossaryEntry = sampleGlossary.get(sampleData.type);
  return _notNil(glossaryEntry?.archived_at);
};

/**
 * Checks if any planned samples contain archived sample types
 */
export const containsArchivedSampleTypes = (
  plannedSamples: ReadonlyMap<TeamGlossaryApiId | TempSampleTypeId, SampleData | TempSampleData>,
  sampleGlossary: Map<TeamGlossaryApiId, SampleGlossaryEntry>
): boolean => {
  return Array.from(plannedSamples.values()).some((sampleData) => isCurrentSampleArchived(sampleData, sampleGlossary));
};
